import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {Lottery} from '../../models';
import {PriceCategory, PriceCategoryAssignedReduction} from '../../models';
import {environment} from '~environments/environment';

/**
 * Service for creating and updating membership entity.
 */
@Injectable({
  providedIn: 'root'
})
export class PriceCategoryService {
  public API_URL: any = environment.envAPI;

  public currentPriceCategoriesList: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public castCurrentPriceCategoriesList: any = this.currentPriceCategoriesList.asObservable();

  public currentPriceCategory: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public castCurrentPriceCategory: any = this.currentPriceCategory.asObservable();

  public currentlyFetching: BehaviorSubject<any> = new BehaviorSubject<any>(false);

  constructor(private http: HttpClient) {
  }

  /**
   * Create price category.
   */
  public get(lottery: Lottery): Observable<Array<PriceCategory>> {
    return this.http.get<Array<PriceCategory>>(`${this.API_URL}/l/lotteries/${lottery.id}/submission-setup/${lottery.submissionSetup.id}/price-categories`).pipe(
      map((response: Array<PriceCategory>) => response['hydra:member'].map(priceCategory => new PriceCategory(priceCategory)))
    );
  }

  /**
   * Fetch price categories.
   */
  public fetchCategories(lottery: any): Observable<any> {
    return this.http.post<any>(`${this.API_URL}/l/price-category/fetch`, lottery).pipe(
      map(_ => true)
    );
  }

  /**
   * Create price category.
   */
  public create(priceCategory: PriceCategory): Observable<PriceCategory> {
    return this.http.post<PriceCategory>(`${this.API_URL}/l/price-categories`, priceCategory).pipe(
      map(res => new PriceCategory(res))
    );
  }

  /**
   * Updates price category.
   */
  public update(priceCategory: PriceCategory): Observable<PriceCategory> {
    return this.http.patch<PriceCategory>(`${this.API_URL}/l/price-categories/${priceCategory.id}`, priceCategory).pipe(
      map(res => new PriceCategory(res))
    );
  }

  /**
   * Deletes price category.
   */
  public delete(priceCategoryId: string): Observable<PriceCategory> {
    return this.http.delete<PriceCategory>(`${this.API_URL}/l/price-categories/${priceCategoryId}`);
  }

  /**
   * Creates and assigns the reduction to the price category
   */
  public createAssignedReduction(priceReduction: any): Observable<any> {
    return this.http.post<any>(`${this.API_URL}/l/price-category-assigned-reductions`, priceReduction).pipe(
      map(res => new PriceCategoryAssignedReduction(res))
    );
  }

  /**
   * Updates the reduction to the price category
   */
  public editAssignedReduction(priceReduction: any, priceReductionId: any): Observable<any> {
    return this.http.patch<any>(`${this.API_URL}/l/price-category-assigned-reductions/${priceReductionId}`, priceReduction).pipe(
      map(res => new PriceCategoryAssignedReduction(res))
    );
  }

  /**
   * Deletes the reduction to the price category
   */
  public deleteAssignedReduction(priceReductionId: any): Observable<any> {
    return this.http.delete<any>(`${this.API_URL}/l/price-category-assigned-reductions/${priceReductionId}`).pipe(
      map(res => res)
    );
  }

  /**
   * Adds values to the observer.
   */
  public setCurrentPriceCategoriesList(priceCategoriesList: any): void {
    this.currentPriceCategoriesList.next(priceCategoriesList);
  }

  /**
   * Adds values to the observer.
   */
  public setCurrentPriceCategory(priceCategory: any): void {
    this.currentPriceCategory.next(priceCategory);
  }

  /**
   * Adds values to the observer.
   */
  public setCurrentlyFetching(status: boolean): void {
    this.currentlyFetching.next(status);
  }

  /**
   * Add a new entry to the price categories list
   */
  public addPriceCategoryList(priceCategory: PriceCategory): void {
    this.currentPriceCategoriesList.getValue().push(priceCategory);
  }

  /**
   * Update a certain entry inside the price categories list
   */
  public updatePriceCategoryList(priceCategory: PriceCategory): void {
    const index = this.currentPriceCategoriesList.getValue().findIndex(el => el.id === priceCategory.id);
    this.currentPriceCategoriesList.getValue()[index] = priceCategory;
  }

  /**
   * Delete a certain entry inside the price categories list
   */
  public deletePriceCategoryList(priceCategoryId: string): void {
    const index = this.currentPriceCategoriesList.getValue().findIndex(el => el.id === priceCategoryId);
    this.currentPriceCategoriesList.getValue().splice(index, 1);
  }
}
